/**
 * @class MCCSearch
 * @extends HTMLElement
 * @classdesc Custom element that initializes and manages an iframe for MCC search functionality.
 *
 * @constructor
 * Creates an instance of MCCSearch and initializes the iframe.
 *
 * @method initIframe
 * Initializes an iframe and appends it to the document body if it doesn't already exist.
 * The iframe is configured to load from 'http://localhost:4001' and is styled to cover the entire viewport.
 * The iframe is initially hidden and has a high z-index to ensure it appears above other elements when displayed.
 */

if (!customElements.get('mcc-search')) {
    class MCCSearch extends HTMLElement {
        constructor() {
            super();
            this.iframeSrc =
                this.getAttribute('iframeSrc') || 'http://localhost:4001';
            this.initIframe();

            this.redirectUrl = this.getAttribute('redirect-url');

            this.STOREFRONT_API_ACCESS_TOKEN = '92a12a61fd6bbd940a9a841958fcb365';
        }

        connectedCallback() {
            if (!this.iframe) {
                return;
            }

            window.addEventListener('message', this.handleMessage.bind(this));
        }

        disconnectedCallback() {
            if (!this.iframe) {
                return;
            }

            window.removeEventListener(
                'message',
                this.handleMessage.bind(this),
            );
        }

        /**
         * Handles messages received from an iframe.
         *
         * @param {MessageEvent} event - The message event received from the iframe.
         * @property {string} event.origin - The origin of the message event.
         * @property {Object} event.data - The data sent with the message event.
         * @property {string} event.data.type - The type of the message event.
         * @property {string} [event.data.color] - The selected color (if type is 'selectColor').
         * @property {string} [event.data.undercoat] - The selected undercoat (if type is 'selectColor').
         *
         * @returns {void}
         */
        async handleMessage(event) {
            if (event.origin !== this.iframeSrc) return;

            const { type } = event.data;

            if (type === 'closeModal') {
                setTimeout(() => {
                    this.iframe.style.display = 'none';
                }, 500);
            }

            if (type === 'selectColor') {
                // // Quick hack to close the modal after selecting a color
                // setTimeout(() => {
                //     this.iframe.style.display = 'none';
                // }, 500);

                const { color, undercoat } = event.data;

                // Json encode and base 64 encode the color and undercoat
                const colorBase64 = btoa(JSON.stringify({color, undercoat}));

                if (color.shopifyProductGuid) {
                    const success = await this.fetchProduct(color.shopifyProductGuid);
                    if (success) {
                        return;
                    }
                }

                if (this.redirectUrl) {
                    // Redirect window to the selected color page
                    window.location.href = `${this.redirectUrl}?color=${colorBase64}`;
                }

                const selectedColorElement =
                    document.querySelector('mcc-selected-color');
                if (selectedColorElement) {
                    selectedColorElement.dispatchEvent(
                        new CustomEvent('colorSelected', {
                            detail: { color, undercoat },
                        }),
                    );
                }
            }
        }

        async fetchProduct(product_guid) {
            const storefrontToken = this.STOREFRONT_API_ACCESS_TOKEN;
            const shop = 'dipyourcar-com.myshopify.com';
            const productGID = product_guid;

            const query = `
              query getProductHandle($id: ID!) {
                node(id: $id) {
                  ... on Product {
                    id
                    handle
                    title
                  }
                }
              }
            `;

            try {
            const response = await fetch(`https://${shop}/api/2025-07/graphql.json`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': storefrontToken
              },
              body: JSON.stringify({
                query,
                variables: {
                  id: productGID
                }
              })
            })

            const data = await response.json();
            const product = data.data.node;
            // redirect to the product page using the handle
                window.location.href = `/products/${product.handle}`;
                return true;
            } catch (error) {
                console.error('Error fetching product:', error);
                return false;
            }
        }


        initIframe() {
            /**
             * Initializes an iframe and appends it to the document body if it doesn't already exist.
             * The iframe is configured to load from the provided iframeSrc attribute and is styled to cover the entire viewport.
             * The iframe is initially hidden and has a high z-index to ensure it appears above other elements when displayed.
             */
            if (!document.querySelector(`iframe[src="${this.iframeSrc}"]`)) {
                const iframe = document.createElement('iframe');

                iframe.src = this.iframeSrc;
                iframe.allow = 'fullscreen';

                iframe.style.position = 'fixed';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100vh';
                iframe.style.border = 'none';
                iframe.style.zIndex = '9999';
                iframe.style.display = 'none';

                iframe.id = 'mcc-iframe';
                iframe.className = 'mcc-iframe';

                this.iframe = iframe;

                document.body.appendChild(iframe);
            }
        }
    }

    customElements.define('mcc-search', MCCSearch);
}

/**
 * @class MCCSearchButton
 * @extends HTMLElement
 * @classdesc Custom element that acts as a button to interact with the MCC search iframe.
 *
 * @constructor
 * Creates an instance of MCCSearchButton, sets up a reference to the iframe, and adds a click event listener.
 *
 * @method connectedCallback
 * Lifecycle method called when the element is added to the DOM. Ensures the button is a child of MCCSearch.
 *
 * @method handleClick
 * Handles the click event on the button. Sends a message to the iframe to open a modal.
 */
if (!customElements.get('mcc-search-button')) {
    class MCCSearchButton extends HTMLElement {
        constructor() {
            super();

            this.iframe = document.querySelector('#mcc-iframe');

            this.addEventListener('click', this.handleClick.bind(this));
            this.message = {
                type: 'openModal',
            };
        }

        connectedCallback() {
            if (!this.closest('mcc-search')) {
                console.error(
                    'MCCSearchButton must be a child of MCCSearch element.',
                );
            }
        }

        handleClick() {
            if (this.iframe) {
                this.iframe.style.display = 'block';

                // wait 100 milliseconds before sending the message to ensure the iframe is ready
                setTimeout(() => {
                    this.iframe.contentWindow.postMessage(this.message, '*');
                }, 100);
            }
        }
    }

    customElements.define('mcc-search-button', MCCSearchButton);

    if (!customElements.get('mcc-advanced-search')) {
        class MCCAdvancedSearch extends MCCSearchButton {
            constructor() {
                super();

                this.message = {
                    type: 'openModal',
                    window: 'advancedSearch',
                };
            }
        }

        customElements.define('mcc-advanced-search', MCCAdvancedSearch);
    }

    if (!customElements.get('mcc-saved-colors')) {
        class MCCSavedColorsButton extends MCCSearchButton {
            constructor() {
                super();

                this.message = {
                    type: 'openModal',
                    window: 'savedColors',
                };
            }
        }

        customElements.define('mcc-saved-colors', MCCSavedColorsButton);
    }
}

/**
 * @class MCCPreMadeOemColor
 * @extends HTMLElement
 * @classdesc Custom element that listens for a message event to select a color.
 *
 * @constructor
 * Creates an instance of MCCSelectedColor and sets up an event listener for message events.
 *
 * @method handleMessage
 * Handles the message event to select a color.
 */
if (!customElements.get('mcc-pre-made-oem-color')) {
    class MCCPreMadeOemColor extends HTMLElement {
        constructor() {
            super();

            this.STOREFRONT_API_ACCESS_TOKEN = '92a12a61fd6bbd940a9a841958fcb365';

            this.colorName = this.getAttribute('color-name');
            this.colorCode = this.getAttribute('color-code');
            this.undercoatProductId = this.getAttribute('undercoat-product-id');

            this.undercoatData = null;

            // select the first variant-selects element on the page
            this.variantSelects = document.querySelector('variant-selects');

            // console.log('Variant selects:', this.variantSelects);

            this.variantSelects.addEventListener('change', this.onVariantChange.bind(this));

            // console.log('Product form selector', this.variantSelects.getAttribute('data-section'));

            // product form
            const dataSection = this.variantSelects.getAttribute('data-section');
            this.productForm = document.getElementById(
                `product-form-${dataSection}`,
            );

            // Product price
            this.productPrice = document.getElementById(
                `Price-${dataSection}`,
            );

            this.resetProductPrice();

            this.undercoatForm = this.querySelector('form#undercoatAddToCart');
        }

        fetchUndercoatProduct() {
            if (!this.undercoatProductId) {
                return;
            }

            const productId = this.undercoatProductId;
            const storefrontAccessToken = this.STOREFRONT_API_ACCESS_TOKEN;
            const shop = 'dipyourcar-com.myshopify.com';

            const query = `
                query getProductVariants {
                    node(id: "gid://shopify/Product/${productId}") {
                    ... on Product {
                        id
                        title
                        metafield(namespace: "custom", key: "oem_image") {
                            value
                        }
                        variants(first: 100) {
                            edges {
                                node {
                                    id
                                    title
                                    sku
                                    price {
                                        amount
                                        currencyCode
                                    }
                                    selectedOptions {
                                        name
                                        value
                                    }
                                }
                            }
                        }
                    }
                }
            }`;

            let buyerCountryCode = 'US';
            if (window.Shopify && window.Shopify.country) {
                buyerCountryCode = window.Shopify.country;
            } else if (window.navigator && window.navigator.language) {
                // Fallback: try to infer from browser language (e.g., "en-US" -> "US")
                const lang = window.navigator.language;
                const match = lang.match(/-([A-Z]{2})$/i);
                if (match) {
                    buyerCountryCode = match[1].toUpperCase();
                }
            }

            fetch(`https://${shop}/api/2025-07/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
                },
                body: JSON.stringify({ query })
            })
            .then(response => response.json())
            .then(data => {
                const productNode = data.data.node;
                const variants = productNode.variants.edges.map(edge => edge.node);
                const undercoatImage = productNode.metafield.value;

                // save data to this.undercoatData
                this.undercoatData = {
                    variants,
                    undercoatImage,
                };

                // update amount for variants as multiple of 100
                this.undercoatData.variants.forEach(variant => {
                    variant.price.amount = Math.round(variant.price.amount * 100);
                });

                this.updateUndercoatId();
                this.updateUndercoatInput();
                this.renderUndercoatInfo();
                this.updateProductPrice();
            })
            .catch(error => {
                console.error('Error fetching product variants:', error);
            });
       }

        resetProductPrice() {
            if (!this.productPrice) {
                return;
            }

            // create a copy of product price without id and append next to product price
            this.newProductPrice = this.productPrice.cloneNode(true);
            this.newProductPrice.removeAttribute('id');

            this.productPrice.parentNode.appendChild(this.newProductPrice);

            // hide the original product price
            this.productPrice.style.display = 'none';
        }

        resetVariantSelects() {
            this.variantSelects.style.setProperty('display', 'block', 'important');

            // Hide the variant with the index priceGroupVariantIndex
            this.variantSelects.querySelectorAll('select, fieldset').forEach((item, index) => {
                if (index === this.priceGroupVariantIndex) {
                    item.style.setProperty('display', 'none', 'important');
                }
            });
        }

        connectedCallback() {
            this.colorUndercoatElement = this.querySelector('[data-undercoat]');
            this.colorUndercoatImgElement = this.querySelector(
                '[data-undercoat-image]',
            );

            this.getVariantData();
            this.updateOptions();
            this.updateMasterId();
            this.fetchUndercoatProduct();
        }

        onVariantChange(event) {
            // console.log('Variant changed:', event);

            this.getVariantData()
            this.updateOptions();
            this.updateMasterId();
            this.updateUndercoatId();
            this.updateUndercoatInput();
            this.updateProductPrice();

            // console.log('Variant data', this.getVariantData());
            // console.log('Variant options:', this.options);
            // console.log('Current variant', this.currentVariant);
            // console.log('Current undercoat', this.currentUndercoatVariant);
        }

        // Product.variants
        getVariantData() {
            this.variantData = this.variantData || JSON.parse(this.variantSelects.querySelector('[type="application/json"]').textContent);
            return this.variantData;
        }

        updateProductPrice() {
            if (!this.newProductPrice || !this.currentVariant) {
                return;
            }

            // console.log('Updating product price');

            let priceTotal = this.currentVariant.price;

            if (this.currentUndercoatVariant) {
                priceTotal += this.currentUndercoatVariant.price.amount;
            }

            this.newProductPrice.querySelector('.price-item').textContent = formatPriceAmount(priceTotal, 'USD');
            this.newProductPrice.style.display = 'block';
        }

        updateOptions() {
            this.options = Array.from(
                this.variantSelects.querySelectorAll("select, fieldset"),
                (item) => {
                    if (item.tagName === "SELECT") {
                        return item.value;
                    } else {
                    return Array.from(item.querySelectorAll("input")).find(
                        (radio) => radio.checked,
                    )?.value;
                }
            });
        }

        updateMasterId() {
            this.currentVariant = this.getVariantData().find((variant) => {
                return !variant.options
                    .map((option, index) => {
                        return this.options[index] === option;
                })
                .includes(false);
            });
        }

        updateUndercoatId() {
            // check OEM_UNDERCOAT_VARIANTS is defined and is an object
            if (!this.undercoatData || typeof this.undercoatData !== 'object') {
                return;
            }

            this.currentUndercoatVariant = this.undercoatData.variants.find((variant) => {
                return !variant.selectedOptions
                    .map((option, index) => {
                        return this.options[index] === option.value;
                })
                .includes(false);
            });
        }

        renderUndercoatInfo() {
            if (!this.undercoatData || !this.currentUndercoatVariant) {
                return;
            }

            if (this.colorUndercoatElement) {
                this.colorUndercoatElement.style.display = 'block';
            }

            if (this.colorUndercoatImgElement) {
                // if undercoatImage is a url, set the background to the url otherwise rgb split by |
                if (this.undercoatData.undercoatImage.startsWith('http')) {
                    this.colorUndercoatImgElement.style.background =
                        `url(${this.undercoatData.undercoatImage}) center center / cover no-repeat`;
                } else {
                    const rgb = this.undercoatData.undercoatImage.split(',');
                    this.colorUndercoatImgElement.style.background = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]}) center center / cover no-repeat`;
                }
                // set center center and cover background size
                this.colorUndercoatImgElement.style.background =
                    this.undercoatData.undercoatImage + ' center center / cover no-repeat';
            }
        }

        updateUndercoatInput() {
            if (!this.undercoatData || !this.currentUndercoatVariant) {
                return;
            }

            // Extract the numeric id from gid://shopify/ProductVariant/[undercoatVariantId]
            const undercoatVariantId = this.currentUndercoatVariant.id.split('/').pop();

            const undercoatInput = this.undercoatForm.querySelector('input[name=id]');
            if (undercoatInput) {
                undercoatInput.value = undercoatVariantId;
            }

            // Update properties[Undercoat For]
            const undercoatForInput = this.undercoatForm.querySelector('input[name="properties[Undercoat For]"]');
            if (undercoatForInput) {
                undercoatForInput.value = `${this.colorName} / ${this.colorCode}`;
            }
        }

        toggleAddButtonStatus(disable = true, text) {
            if (!this.productForm) return;

            const addCartButtons = this.productForm.querySelectorAll(
              'button[type="submit"]',
            );
            const dynamicCheckout = this.productForm.querySelector(".dynamic-checkout"); // 动态结账

            if (disable) {
              addCartButtons.forEach((addButton) => {
                addButton.setAttribute("disabled", "disabled");
                if (!text) return;

                const addButtonTextElement = addButton.querySelector(".button-text");
                if (addButtonTextElement) addButtonTextElement.textContent = text;
              });

              // 隐藏动态支付
              if (dynamicCheckout && text) dynamicCheckout.classList.add("hidden");
            } else {
              addCartButtons.forEach((button) => {
                button.removeAttribute("disabled");

                const addButtonTextElement = button.querySelector(".button-text");
                if (!addButtonTextElement) return;

                addButtonTextElement.textContent = text
                  ? text
                  : window["variantStrings"]["addToCart"];
              });

              // 显示动态结账
              if (dynamicCheckout) dynamicCheckout.classList.remove("hidden");
            }
        }
    }

    customElements.define('mcc-pre-made-oem-color', MCCPreMadeOemColor);
}


/**
 * @class MCCSelectedColor
 * @extends HTMLElement
 * @classdesc Custom element that listens for a message event to select a color.
 *
 * @constructor
 * Creates an instance of MCCSelectedColor and sets up an event listener for message events.
 *
 * @method handleMessage
 * Handles the message event to select a color.
 */
if (!customElements.get('mcc-selected-color')) {
    class MCCSelectedColor extends HTMLElement {
        constructor() {
            super();
            this.style.display = 'none';

            this.color = null;
            this.undercoat = null;

            this.priceGroupVariantName = this.getAttribute('price-code-variant-name');
            // TODO: get the index from the variant-selects element
            this.priceGroupVariantIndex = 0;

            // select the first variant-selects element on the page
            this.variantSelects = document.querySelector('variant-selects');
            this.variantSelects.style.setProperty('display', 'none', 'important');

            // console.log('Variant selects:', this.variantSelects);

            this.variantSelects.addEventListener('change', this.onVariantChange.bind(this));

            // console.log('Product form selector', this.variantSelects.getAttribute('data-section'));

            // product form
            const dataSection = this.variantSelects.getAttribute('data-section');
            this.productForm = document.getElementById(
                `product-form-${dataSection}`,
            );
            this.toggleAddButtonStatus(true, "");

            // Product price
            this.productPrice = document.getElementById(
                `Price-${dataSection}`,
            );

            this.resetProductPrice();

            this.undercoatForm = this.querySelector('form#undercoatAddToCart');

            this.boundColorSelectedHandler = this.onColorSelected.bind(this);
        }

        resetProductPrice() {
            if (!this.productPrice) {
                return;
            }

            // create a copy of product price without id and append next to product price
            this.newProductPrice = this.productPrice.cloneNode(true);
            this.newProductPrice.removeAttribute('id');

            this.productPrice.parentNode.appendChild(this.newProductPrice);

            // hide the original product price
            this.productPrice.style.display = 'none';
        }

        resetVariantSelects() {
            this.variantSelects.style.setProperty('display', 'block', 'important');

            // Hide the variant with the index priceGroupVariantIndex
            this.variantSelects.querySelectorAll('select, fieldset').forEach((item, index) => {
                if (index === this.priceGroupVariantIndex) {
                    item.style.setProperty('display', 'none', 'important');
                }
            });
        }

        connectedCallback() {
            this.colorCodeElement = this.querySelector('[data-color-code]');
            this.colorNameElement = this.querySelector('[data-color-name]');
            this.colorMakeElement = this.querySelector('[data-color-make]');
            this.colorYearElement = this.querySelector('[data-color-year]');
            this.colorImgElement = this.querySelector('[data-color-image]');
            this.colorPriceCodeElement =
                this.querySelector('[data-price-code]');
            this.colorUndercoatElement = this.querySelector('[data-undercoat]');
            this.colorUndercoatCodeElement = this.querySelector(
                '[data-undercoat-code]',
            );
            this.colorUndercoatNameElement = this.querySelector(
                '[data-undercoat-name]',
            );
            this.colorUndercoatImgElement = this.querySelector(
                '[data-undercoat-image]',
            );
            this.colorUndercoatPriceCodeElement = this.querySelector(
                '[data-undercoat-price-code]',
            );

            this.addEventListener(
                'colorSelected',
                this.boundColorSelectedHandler,
            );

            this.checkUrl();

            // to be deleted
            const selectBtn = this.querySelector("button");
            if (selectBtn) {
                // Add click
                selectBtn.addEventListener("click", this.selectColor.bind(this));
            }
        }

        checkUrl() {
            const urlParams = new URLSearchParams(window.location.search);
            const colorBase64 = urlParams.get('color');

            if (!colorBase64) {
                return;
            }

            let detail = null;
            try {
                detail = JSON.parse(atob(colorBase64));
            } catch (e) {
                console.error('Error parsing color:', e);

                return;
            }

            if (detail) {
                this.dispatchEvent(
                    new CustomEvent('colorSelected', {
                        detail: detail,
                    }),
                );
              this.toggleAddButtonStatus(false, "");
            }
        }

        selectColor() {
            const color = {
                "code": "0172",
                "name": "VIOLA PARISEA P.",
                "brand": "LAMBORGHINI",
                "imgSrc": "url('http://spectro-images.e-mixing.eu/colors/14003359/1518076/637220436941275852.jpg')",
                "imgAlt": "VIOLA PARISEA P.",
                "year": "2017/",
                "tricoat": true,
                "candy": false,
                "xyralic": false,
                "silver": false,
                "priceCode": "E"
            }

            const undercoat = {
                "code": "U104",
                "name": "U104",
                "imgSrc": "url('http://spectro-images.e-mixing.eu/colors/141673/1501402/637221396214840543.jpg')",
                "imgAlt": "U104",
                "priceCode": "B"
            };

            this.dispatchEvent(
                new CustomEvent('colorSelected', {
                    detail: { color, undercoat },
                })
            )
        }

        onColorSelected(event) {
            const { color, undercoat } = event.detail;

            this.resetColorElementsStatus();

            this.color = color;
            this.undercoat = undercoat;

            // Json encode and base 64 encode the color and undercoat
            // const colorBase64 = btoa(JSON.stringify({color, undercoat}));
            // console.log('Color selected:', colorBase64);


            // console.log('Color selected:', color, undercoat);

            this.renderColorSelection();
            this.resetVariantSelects();
            this.addProductProperties();

            this.updateVariant();
        }

        renderColorSelection() {
            if (!this.color) {
                return;
            }

            if (this.colorCodeElement) {
                this.colorCodeElement.textContent = this.color.code;
            }

            if (this.colorNameElement) {
                this.colorNameElement.textContent = this.color.name;
            }

            if (this.colorMakeElement) {
                this.colorMakeElement.textContent = this.color.brand;
            }

            if (this.colorYearElement) {
                this.colorYearElement.textContent = this.color.year;
            }

            if (this.colorImgElement) {
                // set center center and cover background size
                this.colorImgElement.style.background =
                    this.color.imgSrc + ' center center / cover';
            }

            if (this.colorPriceCodeElement) {
                this.colorPriceCodeElement.textContent = this.color.priceCode;
            }

            this.updateUndercoatInput();
            this.renderUndercoatInfo();

            this.style.display = 'block';

            // scroll to the selected color -100px from the top
            window.scrollTo({
                top: this.offsetTop - 100,
                behavior: 'smooth',
            });
        }

        updateVariant() {
            if (!this.color) {
                return;
            }

            // Select input with name priceGroupVariantName and value color.priceCode
            const priceGroupVariantInput = document.querySelector(
                `input[name="${this.priceGroupVariantName}"][value="${this.color.priceCode}"]`,
            );

            // mark as checked
            if (priceGroupVariantInput) {
                // console.log('Price group variant input:', priceGroupVariantInput);
                priceGroupVariantInput.checked = true;

                //trigger change event
                // console.log('Dispatch Variants Change Event');
                priceGroupVariantInput.dispatchEvent(new Event('change', {bubbles: true}));
            }
        }

        addProductProperties() {
            if (!this.color || !this.productForm) {
                return;
            }

            // console.log('Adding product properties:', this.color);

            // Create shopify product properties for color { name, code, brand, year, formula_id }
            const productProperties = {
                'Color Name': this.color.name,
                'Color Code': this.color.code,
                'Make': this.color.brand,
                'Year': this.color.year,
            };
            
            // Add MCC Cloud Formula ID if it exists (underscore prefix hides from customers)
            if (this.color.formula_id) {
                productProperties['_MCC Cloud Formula ID'] = this.color.formula_id;
            }

            // add input fields to the form
            Object.entries(productProperties).forEach(([name, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = `properties[${name}]`;
                input.value = value;

                this.productForm.appendChild(input);
            });
        }


        onVariantChange(event) {
            // console.log('Variant changed:', event);

            this.getVariantData()
            this.updateOptions();
            this.updateMasterId();
            this.updateUndercoatId();
            this.updateUndercoatInput();
            this.renderUndercoatInfo();
            this.updateProductPrice();

            // console.log('Variant data', this.getVariantData());
            // console.log('Variant options:', this.options);
            // console.log('Current variant', this.currentVariant);
            // console.log('Current undercoat', this.currentUndercoatVariant);
        }

        // Product.variants
        getVariantData() {
            this.variantData = this.variantData || JSON.parse(this.variantSelects.querySelector('[type="application/json"]').textContent);
            return this.variantData;
        }

        updateProductPrice() {
            if (!this.newProductPrice || !this.currentVariant) {
                return;
            }

            // console.log('Updating product price');

            let priceTotal = this.currentVariant.price;

            if (this.currentUndercoatVariant) {
                priceTotal += this.currentUndercoatVariant.price;
            }

            this.newProductPrice.querySelector('.price-item').textContent = formatPriceAmount(priceTotal, 'USD');
        }

        updateOptions() {
            this.options = Array.from(
                this.variantSelects.querySelectorAll("select, fieldset"),
                (item) => {
                    if (item.tagName === "SELECT") {
                        return item.value;
                    } else {
                    return Array.from(item.querySelectorAll("input")).find(
                        (radio) => radio.checked,
                    )?.value;
                }
            });
        }

        updateMasterId() {
            this.currentVariant = this.getVariantData().find((variant) => {
                return !variant.options
                    .map((option, index) => {
                        return this.options[index] === option;
                })
                .includes(false);
            });
        }

        updateUndercoatId() {
            // check OEM_UNDERCOAT_VARIANTS is defined and is an object
            if (!OEM_UNDERCOAT_VARIANTS || typeof OEM_UNDERCOAT_VARIANTS !== 'object') {
                return;
            }

            if (!this.undercoat ) {
                this.currentUndercoatVariant = null;
                return;
            }

            // console.log('Undercoat variants', OEM_UNDERCOAT_VARIANTS);

            this.currentUndercoatVariant = OEM_UNDERCOAT_VARIANTS.find((variant) => {
                return !variant.options
                    .map((option, index) => {
                        if (index === this.priceGroupVariantIndex) {
                            return this.undercoat.priceCode === option;
                        }

                        return this.options[index] === option;
                })
                .includes(false);
            });
        }

        renderUndercoatInfo() {
            if (!this.undercoat || !this.currentUndercoatVariant) {
                return;
            }


            if (this.colorUndercoatElement) {
                this.colorUndercoatElement.style.display = 'block';
            }

            if (this.colorUndercoatCodeElement) {
                this.colorUndercoatCodeElement.textContent = this.undercoat.code;
            }

            if (this.colorUndercoatNameElement) {
                this.colorUndercoatNameElement.textContent = this.undercoat.name;
            }

            if (this.colorUndercoatImgElement) {
                // set center center and cover background size
                this.colorUndercoatImgElement.style.background =
                    this.undercoat.imgSrc + ' center center / cover no-repeat';
            }

            if (this.colorUndercoatPriceCodeElement) {
                this.colorUndercoatPriceCodeElement.textContent =
                    this.undercoat.priceCode;
            }

            // Update price
            if (this.colorUndercoatElement.querySelector('.price-item')) {
                this.colorUndercoatElement.querySelector('.price-item').textContent = '+ ' +
                    formatPriceAmount(this.currentUndercoatVariant.price, 'USD');
            }
        }

        updateUndercoatInput() {
            if (!this.color || !this.undercoat || !this.undercoatForm || !this.currentUndercoatVariant) {
                return;
            }

            const undercoatInput = this.undercoatForm.querySelector('input[name=id]');
            if (undercoatInput) {
                undercoatInput.value = this.currentUndercoatVariant.id;
            }

            // Update properties[Undercoat Name]
            const undercoatNameInput = this.undercoatForm.querySelector('input[name="properties[Undercoat Name]"]');
            if (undercoatNameInput) {
                undercoatNameInput.value = this.undercoat.name;
            }

            // Update properties[Undercoat For]
            const undercoatForInput = this.undercoatForm.querySelector('input[name="properties[Undercoat For]"]');
            if (undercoatForInput) {
                undercoatForInput.value = `${this.color.name} / ${this.color.code}`;
            }
        }

        resetUndercoatInput() {
            if (!this.undercoatForm) {
                return;
            }

            const undercoatInput = this.undercoatForm.querySelector('input[name=id]');
            if (undercoatInput) {
                undercoatInput.value = '';
            }

            // Update properties[Undercoat Name]
            const undercoatNameInput = this.undercoatForm.querySelector('input[name="properties[Undercoat Name]"]');
            if (undercoatNameInput) {
                undercoatNameInput.value = '';
            }

            // Update properties[Undercoat For]
            const undercoatForInput = this.undercoatForm.querySelector('input[name="properties[Undercoat For]"]');
            if (undercoatForInput) {
                undercoatForInput.value = '';
            }
        }

        displayCartDrawer() {
            const cartDrawer = document.getElementById('Cart-Drawer');

            if (cartDrawer && !cartDrawer.hasAttribute('data-status-silence')) {
                cartDrawer.show();
            } else {
                return (window.location = window['routes']['cart_url']);
            }
        }

        toggleAddButtonStatus(disable = true, text) {
            if (!this.productForm) return;

            const addCartButtons = this.productForm.querySelectorAll(
              'button[type="submit"]',
            );
            const dynamicCheckout = this.productForm.querySelector(".dynamic-checkout"); // 动态结账

            if (disable) {
              addCartButtons.forEach((addButton) => {
                addButton.setAttribute("disabled", "disabled");
                if (!text) return;

                const addButtonTextElement = addButton.querySelector(".button-text");
                if (addButtonTextElement) addButtonTextElement.textContent = text;
              });

              // 隐藏动态支付
              if (dynamicCheckout && text) dynamicCheckout.classList.add("hidden");
            } else {
              addCartButtons.forEach((button) => {
                button.removeAttribute("disabled");

                const addButtonTextElement = button.querySelector(".button-text");
                if (!addButtonTextElement) return;

                addButtonTextElement.textContent = text
                  ? text
                  : window["variantStrings"]["addToCart"];
              });

              // 显示动态结账
              if (dynamicCheckout) dynamicCheckout.classList.remove("hidden");
            }
        }

        resetColorElementsStatus() {
            if (this.colorCodeElement) {
                this.colorCodeElement.textContent = "";
            }

            if (this.colorNameElement) {
                this.colorNameElement.textContent = "";
            }

            if (this.colorMakeElement) {
                this.colorMakeElement.textContent = "";
            }

            if (this.colorYearElement) {
                this.colorYearElement.textContent = "";
            }

            if (this.colorImgElement) {
                this.colorImgElement.style.background = "";
            }

            if (this.colorPriceCodeElement) {
                this.colorPriceCodeElement.textContent = "";
            }

            if (this.colorUndercoatElement) {
                this.colorUndercoatElement.style.display = "none";
            }

            if (this.colorUndercoatCodeElement) {
                this.colorUndercoatCodeElement.textContent = "";
            }

            if (this.colorUndercoatNameElement) {
                this.colorUndercoatNameElement.textContent = "";
            }

            if (this.colorUndercoatImgElement) {
                this.colorUndercoatImgElement.style.background = "";
            }

            if (this.colorUndercoatPriceCodeElement) {
                this.colorUndercoatPriceCodeElement.textContent = "";
            }

            this.resetUndercoatInput();
        }
    }

    customElements.define('mcc-selected-color', MCCSelectedColor);
}

if (!customElements.get('mcc-search-input')) {
    class MCCSearchInput extends HTMLElement {
        constructor() {
            super();

            this.iframe = document.querySelector('#mcc-iframe');

            this.boundFormSubmitHandler = this.onFormSubmit.bind(this);
        }

        connectedCallback() {
            this.form = this.querySelector('form');
            if (this.form) {
                this.form.addEventListener(
                    'submit',
                    this.boundFormSubmitHandler,
                );
            }
        }

        disconnectedCallback() {
            if (this.form) {
                this.form.removeEventListener(
                    'submit',
                    this.boundFormSubmitHandler,
                );
            }
        }

        onFormSubmit(event) {
            event.preventDefault();
            const formData = new FormData(this.form);
            const searchQuery = formData.get('searchQuery');

            if (!searchQuery) {
                return;
            }

            const message = {
                type: 'openModal',
                window: 'colorSearch',
                searchQuery,
            };

            if (this.iframe) {
                this.iframe.style.display = 'block';

                // wait 100 milliseconds before sending the message to ensure the iframe is ready
                setTimeout(() => {
                    this.iframe.contentWindow.postMessage(message, '*');
                }, 100);
            }
        }
    }

    customElements.define('mcc-search-input', MCCSearchInput);
}

/**
 * 处理添加购物车操作
 */
if (!customElements.get('mcc-product-form')) {
    customElements.define(
        'mcc-product-form',
        class MCCProductForm extends HTMLElement {
            constructor() {
                super();
                this.form = this.querySelector('form');
                if(!this.form) return;

                this.isloading = false;
                this.error = false;
                this.variantIdInput.disabled = false;
                this.form.addEventListener('submit', this.onSubmitHandler.bind(this)); // 添加提交购物车监听

                this.addCartButtons = this.querySelectorAll('button[type="submit"]'); // 添加购物车按钮，可能有多处按钮

                this.cartDrawer = document.getElementById('Cart-Drawer');
                if (this.cartDrawer && !this.cartDrawer.hasAttribute('data-status-silence')) this.addCartButtons.forEach(button=>{
                    button.setAttribute('aria-haspopup', 'dialog');
                    button.setAttribute('aria-expanded', 'false');
                    button.setAttribute('aria-controls', 'Cart-Drawer');
                });

                this.errorMessageWrapper = document.getElementById(`Product-Form-Error-Message-${this.dataset.section}`);
                // 快速购物弹窗
                this.quickAddDrawer = this.closest('product-side-drawer');

                // 迷你结账
                this.miniChecker = document.getElementById(`Product-Mini-Checkout-${this.dataset.section}`);
                if(this.miniChecker) {
                    this.miniCheckerObserver = new IntersectionObserver((entries, observer)=>{
                        if(entries[0].isIntersecting) {
                            this.hideMiniCheck();
                        } else if (entries[0].boundingClientRect.top < 0){
                            this.showMiniCheck();
                        }
                    }, {
                        root: null,
                        rootMargin: '-200px 0px 0px 0px'
                    });

                    this.miniCheckerObserver.observe(this);
                }
            }

            disconnectedCallback() {
                if(this.miniCheckerObserver) this.miniCheckerObserver.disconnect();
            }

            get variantIdInput() {
                return this.form.querySelector('[name=id]');
            }

            /**
             * 是否隐藏错误信息
             * 当产品是礼品卡的时候，隐藏错误信息
             * @returns {boolean}
             */
            get hideErrors() {
                return this.dataset.hideErrors === 'true';
            }

            /**
             * 处理点击提交购物车
             * @param event
             */
            async onSubmitHandler(event) {
                if(this.isloading) return;
                event.preventDefault();

                if(Array.from(this.addCartButtons).find(button => button.hasAttribute('disabled'))) return;

                this.handleErrorMessage();
                this.startLoading();

                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];  /* 浏览器会自动为 formData 对象生成带有边界(boundary)的 multipart/form-data 类型*/

                const formData = new FormData(this.form);
                formData.append('sections', this.getSectionsToRender().map((section)=>section.section).join());
                formData.append('sections_url', window.location.pathname);
                config.body = formData;

                // console.log('Form data to commit', config);

                // Add undercoat
                await this.addUndercoatToCart();

                if (this.error) {
                    this.endLoading();
                    return;
                }

                fetch(`${window['routes']['cart_add_url']}`, config)
                    .then((response) => {
                        return response.json();
                    })
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);
                            return this.error = true;
                        }
                        this.error = false;

                        // 动态更新Sections内容
                        SectionDynamicUpdate.updateSections(this.getSectionsToRender(), response['sections']);

                        publish(PUB_SUB_EVENTS.cartUpdate, {
                            source: 'product-form',
                            productVariantId: formData.get('id'),
                            cartData: response,
                        });

                        if(this.cartDrawer && !this.cartDrawer.hasAttribute('data-status-silence')) {
                            // 打开抽屉
                            this.cartDrawer.show(event.submitter);
                        } else {
                            return window.location = window['routes']['cart_url'];
                        }
                    })
                    .catch((e) => {
                        this.handleErrorMessage(window['variantStrings']['unknownError']);
                        this.error = true;
                    })
                    .finally(()=>{
                        this.endLoading();
                        // 关闭迷你结账
                        this.hideMiniCheck();
                    });
            }

            async addUndercoatToCart() {
                // Select form with id undercoatAddToCart
                const undercoatForm = document.querySelector('form#undercoatAddToCart');
                if (!undercoatForm) return;

                // Select input with name id
                const undercoatIdInput = undercoatForm.querySelector('input[name=id]');
                if (!undercoatIdInput || !undercoatIdInput.value) return;

                const undercoatProperties = {
                    // 'Undercoat Name': undercoatForm.querySelector('input[name="properties[Undercoat Name]"]').value,
                    'Undercoat For': undercoatForm.querySelector('input[name="properties[Undercoat For]"]').value,
                };

                // Select quantity from product form
                const quantity = this.form.querySelector('input[name=quantity]').value;

                const undercoatProduct = {
                    id: undercoatIdInput.value,
                    quantity: quantity,
                    properties: undercoatProperties,
                }

                // Add to cart as JSON
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(undercoatProduct),
                });

                // console.log('Undercoat add to cart response', response);

                if (!response.ok) {
                    this.handleErrorMessage(window['variantStrings']['unknownError']);
                    return this.error = true;
                }
            }

            /**
             * 获取需要重新渲染的位置
             * @returns {[{section: string, selector: string, id: string}]}
             */
            getSectionsToRender() {
                const sections = [
                    {
                        id: 'Cart-Icon-Bubble',
                        section: 'cart-icon-bubble',
                        selector: '.shopify-section'
                    }
                ];

                if(this.cartDrawer && !this.cartDrawer.hasAttribute('data-status-silence')) {
                    sections.push({
                        id: 'Cart-Drawer',
                        section: this.cartDrawer.dataset.section,
                        selector: '#Cart-Drawer-Details',
                    });
                }

                return sections;
            }

            /**
             * 错误显示
             * @param errorMessage
             */
            handleErrorMessage(errorMessage = null) {
                if (this.hideErrors || !this.errorMessageWrapper) return;

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
                this.errorMessageWrapper.textContent = errorMessage ? errorMessage : '';
            }

            startLoading() {
                this.isloading = true;

                this.addCartButtons.forEach(button => {
                    button.setAttribute('disabled', 'disabled');
                    button.classList.add('loading');
                });
            }

            endLoading() {
                this.isloading = false;

                this.addCartButtons.forEach(button => {
                    button.removeAttribute('disabled');
                    button.classList.remove('loading');
                });

                // 关闭快速预览
                if(!this.error && this.quickAddDrawer) {
                    this.quickAddDrawer.hide();
                }
            }

            showMiniCheck() {
                if(this.miniChecker) this.miniChecker.classList.add('active');
            }

            hideMiniCheck() {
                if(this.miniChecker) this.miniChecker.classList.remove('active');
            }
        }
    );
}
